import { Trans, useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
import Button from '@/components/button/Button';
import { styles } from './styles';
import { SignUpProps, UserSignConfirmCode } from '../SignUpInterface';
import TextField from '@/components/textField/TextField';
import { useEffect, useState } from 'react';
import { formatTime } from '@/pages/forgotPassword/counter';
import { SignUpCodeController as SignUpController } from './SignUpCodeController';
import { useUserService } from '@/data/services/useUserService';
import { AppError } from '@/utils/AppError';
import WarningField from '@/components/warningField/WarningField';
import { useNavigate } from 'react-router-dom';
import { getFormattedLanguage } from '@/utils/formatedLanguage';
import { StorageHandler } from '@/data/storage/StorageHandler';

const SignUpCode = ({
  changeStep,
  user,
  loading,
  setLoading,
  showWarning,
  setShowWarning,
  warningMessage,
  setWarningMessage,
  forceResend,
}: SignUpProps) => {
  const { t } = useTranslation([
    'signInCode',
    'create_new_password',
    'auth',
    'common',
  ]);
  const navigate = useNavigate();
  const { isValid, controlledTextView, handleSubmit } = SignUpController();
  const { resendVerificationCode, confirmSignUpCode } = useUserService();
  const minMinutes = 30;
  const [counter, setCounter] = useState(minMinutes);
  const [canResend, setCanResend] = useState(false);
  const [previousTimer, setPreviousTimer] = useState<string | null>(
    StorageHandler.timerCountdown()
  );

  const handleResend = (forceResend: boolean = false) => {
    if (canResend || forceResend) {
      resendVerificationCode(user.email)
        .then(() => {
          setShowWarning(true);
          setPreviousTimer(null);
          setCountDown();
          setWarningMessage(
            t('create_new_password:code_resent_info', { email: user.email })
          );
          setCounter(minMinutes);
        })
        .catch(() => {
          setShowWarning(true);
          setWarningMessage(t('create_new_password:code_resend_error'));
        });
      setCanResend(false);
    }
  };

  const setCountDown = () => {
    const date = new Date();
    date.setSeconds(date.getSeconds() + minMinutes);
    StorageHandler.setTimerCountdown(date.toString());
  };

  const onSubmit = (data: UserSignConfirmCode) => {
    setLoading(true);
    confirmSignUpCode(
      user.email,
      user.password,
      data.code,
      getFormattedLanguage()
    )
      .then(() => {
        StorageHandler.clearTimerCountdown();
        changeStep(true);
      })
      .catch((error: AppError) => {
        setShowWarning(true);
        setWarningMessage(t(error.message));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (previousTimer == null) {
      if (forceResend) handleResend(forceResend);
      setCountDown();
    } else {
      const dateFromPreviousDate = new Date(previousTimer).getTime();
      if (new Date().getTime() > dateFromPreviousDate) {
        setCanResend(true);
        setCounter(0);
        setPreviousTimer(null);
      } else {
        const newTime = Math.floor(
          (dateFromPreviousDate - new Date().getTime()) / 1000
        );
        setCounter(newTime);
      }
    }
  }, []);

  useEffect(() => {
    if (counter > 0) {
      const timer = setTimeout(() => setCounter(counter - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      StorageHandler.setTimerCountdown('');
      setCanResend(true);
    }
  }, [counter]);

  const subtitleEmail = (
    <Box>
      <Typography sx={styles.subtitleEmail}>
        <Trans
          i18nKey="create_new_password:email_sent_code"
          values={{ email: user.email }}
          components={{ strong: <strong /> }}
        />
      </Typography>
    </Box>
  );

  const resendCode = (
    <Box>
      <Box sx={styles.sameLineText}>
        <Typography sx={styles.forgetCodeText}>
          {t('create_new_password:expiration_time')}
        </Typography>
        <Typography sx={styles.forgetCode}>
          {counter > 0 ? formatTime(counter) : '00:00'}
        </Typography>
      </Box>
      <Box sx={styles.sameLineText}>
        <Typography sx={styles.forgetCodeText}>
          {t('create_new_password:not_received_code')}{' '}
        </Typography>
        <Typography
          onClick={() => {
            handleResend();
          }}
          style={canResend ? styles.canResend : styles.cantResend}
        >
          {t('create_new_password:resend_code')}
        </Typography>
      </Box>
    </Box>
  );

  const inputFields = (
    <Stack spacing={2}>
      {controlledTextView(
        <TextField
          title={t('create_new_password:title_code_verification')}
          placeholder={t('create_new_password:title_code_verification')}
          disabled={loading}
        />,
        'code'
      )}
    </Stack>
  );

  return (
    <Stack
      spacing={4}
      sx={styles.container}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      {subtitleEmail}
      {inputFields}
      {resendCode}
      <WarningField
        description={warningMessage}
        isHidden={!showWarning}
        setShowWarning={setShowWarning}
        status="warning"
        leftButtonTitle={t('auth:warning_left_button')}
        leftButtonAction={() => navigate('/')}
        rightButtonTitle={t('close')}
        rightButtonAction={() => setShowWarning(false)}
      />
      <Button
        title={t('common:verify')}
        onClick={handleSubmit(onSubmit)}
        disable={!isValid || loading}
        loading={loading}
      />
    </Stack>
  );
};

export default SignUpCode;
